import {
    UseCheckoutPricingReturn,
    UseCheckoutPricingState,
    UseRecurlyInstance,
} from '@recurly/react-recurly';
import { FormBuilderRef } from '@tn/formbuilder/hooks/useFormBuilder';
import type { CouponStringStoreType } from '@tn/foundation/entities/coupon/types';
import {
    GetUserSubscriptionQuery,
    Maybe,
    ProductResponse,
    RedeemCouponResponse,
} from '@tn/foundation/graphql/__generated/gql/graphql';
import { createConsoleLogger } from '@tn/foundation/utils/devtools/console-logger';
import request, { RequestDocument } from 'graphql-request';
import {
    AnyEventObject,
    AnyStateConfig,
    assertEvent,
    DoneActorEvent,
    enqueueActions,
    ErrorActorEvent,
    fromPromise,
    Mapper,
    OutputFrom,
    setup,
} from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import type { directFlowContext } from '../direct';
import { ensureGetUser, getUser } from '../helpers';

import {
    productQuery,
    redeemCouponCacheKey,
    redeemCouponQuery,
    userSubscriptionCacheKey,
    userSubscriptionQuery,
} from './graphql/constants';
import { trackCouponRedemptionFailure, trackCouponRedemptionSuccess } from './tracking';

const { log, error: logError } = createConsoleLogger('flows:redeemCoupon');

export type rcFlowContextInputs = {
    couponCode: string;
    formBuilderRef: Maybe<FormBuilderRef>;
    userPlan: ProductResponse;
    url: string;
    recurlyInstance: UseRecurlyInstance;
    store: CouponStringStoreType;
    recurlyPricing: UseCheckoutPricingState['price'];
};

export type rcFlowContextOutputs = {
    coupon?: CouponStringStoreType['couponList'][0];
    userPlan: ProductResponse;
};

export const submitRedeemCouponActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<void, Pick<rcFlowContextInputs, 'couponCode'>>(async ({ input }) => {
            const cacheKey = 'redeem-coupon';
            const res = await queryClient.fetchQuery<{ redeemCoupon: RedeemCouponResponse }>({
                queryKey: [cacheKey],

                queryFn: () => {
                    const requestHeaders = {
                        'request-source': redeemCouponCacheKey,
                    };
                    return request(
                        `/api/tn/app-shell/graphql/`,
                        redeemCouponQuery as RequestDocument,
                        { couponCode: input.couponCode },
                        {
                            ...requestHeaders,
                        },
                    );
                },
            });
            if (res?.redeemCoupon?.success) {
                log('Coupon redeemed successfully', res);
                return Promise.resolve();
            }
            logError('Coupon not redeemed', res);
            return Promise.reject(new Error('Coupon not redeemed'));
        }),

    inputActor: <
        Mapper<
            rcFlowContextInputs,
            AnyEventObject,
            Pick<rcFlowContextInputs, 'couponCode'>,
            AnyEventObject
        >
    >(({ context }) => {
        return { couponCode: context.couponCode };
    }),
};

export const validationCouponActor: FlowEntity = {
    initActor: ({ queryClient, t }) =>
        fromPromise<
            Pick<rcFlowContextOutputs, 'coupon'>,
            Pick<
                rcFlowContextInputs,
                'couponCode' | 'recurlyInstance' | 'store' | 'recurlyPricing' | 'userPlan'
            >
        >(async ({ input }) => {
            const recurlySubscriptionInstance = input.recurlyInstance.Pricing.Subscription();
            const recurlyPricing = input.recurlyPricing;
            if (!input.couponCode) {
                return Promise.reject(new Error('No coupon provided'));
            }
            const { code, symbol } = recurlyPricing.currency;
            const user = getUser(queryClient);

            await input.store.consolidatedFetchCoupon(
                input.couponCode,
                input.userPlan.billingVendorProductCode || '',
                recurlySubscriptionInstance,
                t,
                input.userPlan.planType,
                !!user?.isSubscriber,
                !!user?.isExSubscriber,
                code,
                symbol,
            );

            const coupon = input.store.getCouponItem(
                input.couponCode,
                input.userPlan.billingVendorProductCode || '',
            );

            const isCouponFreeTrial = coupon?.normalizedCoupon?.couponType === 'free_trial';

            if (coupon?.loading || coupon?.error || isCouponFreeTrial) {
                return Promise.reject(new Error('Coupon not valid'));
            }

            return {
                coupon,
            };
        }),

    inputActor: <
        Mapper<
            rcFlowContextInputs,
            AnyEventObject,
            Pick<
                rcFlowContextInputs,
                'couponCode' | 'recurlyInstance' | 'store' | 'recurlyPricing' | 'userPlan'
            >,
            AnyEventObject
        >
    >(({ context }) => {
        return {
            couponCode: context.couponCode,
            recurlyInstance: context.recurlyInstance,
            recurlyPricing: context.recurlyPricing,
            store: context.store,
            userPlan: context.userPlan,
        };
    }),
};

export const productRetrievalActor: FlowEntity = {
    initActor: ({ queryClient }) =>
        fromPromise<Pick<rcFlowContextOutputs, 'userPlan'>, void>(async () => {
            const user = await ensureGetUser(queryClient);

            let hasForeverCoupon = false;
            try {
                const userSubscription = await queryClient.fetchQuery<GetUserSubscriptionQuery>({
                    queryKey: [userSubscriptionCacheKey],
                    queryFn: () =>
                        request(
                            `/api/tn/app-shell/graphql/`,
                            userSubscriptionQuery as RequestDocument,
                            {},
                            { 'request-source': userSubscriptionCacheKey },
                        ),
                });
                hasForeverCoupon = !!userSubscription?.userSubscription?.activeCoupons?.some(
                    (coupon) => coupon?.duration === 'forever',
                );
            } catch (e) {
                // Fail open: a subscription-lookup failure must not surface the invalid-coupon
                // experience to otherwise-eligible users. Backend redemption still validates.
                logError('Forever-coupon gate: userSubscription lookup failed, allowing flow', e);
            }
            if (hasForeverCoupon) {
                throw new Error('Forever coupon user cannot redeem new coupon');
            }

            const [, productCode] = (
                user?.userFullProfile.entitlement?.multiPackageTracking || ':'
            ).split(':');
            const getProductCacheKey = 'validate-coupon-get-product';
            const productResponse = await queryClient.fetchQuery<{
                product: ProductResponse;
            }>({
                queryKey: [getProductCacheKey],
                queryFn: () => {
                    const requestHeaders = {
                        'request-source': getProductCacheKey,
                    };

                    return request(
                        `/api/tn/signup/graphql/`,
                        productQuery as RequestDocument,
                        {
                            productCode,
                            userRegistrationRegion: user?.userFullProfile
                                .userRegistrationCountry as string,
                        },
                        {
                            ...requestHeaders,
                        },
                    );
                },
            });
            const { product } = productResponse;

            return {
                userPlan: product,
            };
        }),
};

export const redeemCouponFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, trackEvent, t } = rawParams as InitMachineParams;
        return setup({
            types: {
                events: {} as
                    | { type: 'xstate.init'; input: { couponCode: string; url: string } }
                    | { type: 'SUBMIT_CLICK' }
                    | { type: 'GO_TO_SUMMARY' }
                    | {
                          type: 'xstate.done.actor.invalidCoupon';
                      }
                    | {
                          type: 'ATTACH_RECURLY_INSTANCE';
                          params: {
                              recurlyInstance: UseRecurlyInstance;
                              recurlyPricing: UseCheckoutPricingReturn;
                              store: CouponStringStoreType;
                          };
                      }
                    | {
                          type: 'CONNECT_FORMBUILDER_REF';
                          params: FormBuilderRef;
                      }
                    | { type: 'SUBMITTING' }
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof submitRedeemCouponActor.initActor>>,
                          'submitting'
                      >
                    | ErrorActorEvent<
                          OutputFrom<ReturnType<typeof submitRedeemCouponActor.initActor>>,
                          'submitting'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof validationCouponActor.initActor>>,
                          'validationCoupon'
                      >
                    | DoneActorEvent<
                          OutputFrom<ReturnType<typeof productRetrievalActor.initActor>>,
                          'productRetrieval'
                      >,
            },
            guards: {},
            actions: {
                attachRecurlyInstance: enqueueActions(({ enqueue, check: _check, event }) => {
                    assertEvent(event, 'ATTACH_RECURLY_INSTANCE');
                    const { recurlyInstance, store, recurlyPricing } = event.params;
                    enqueue.assign({ recurlyInstance, store, recurlyPricing });
                }),
                persistCouponCode: enqueueActions(({ enqueue, check: _check, event }) => {
                    assertEvent(event, 'xstate.init');
                    const { couponCode } = event.input;
                    enqueue.assign({ couponCode });
                }),
                persistPlan: enqueueActions(({ enqueue, check: _check, event }) => {
                    assertEvent(event, 'xstate.done.actor.productRetrieval');
                    const { userPlan } = event.output;
                    enqueue.assign({ userPlan });
                }),
                persistCoupon: enqueueActions(({ enqueue, check: _check, event }) => {
                    assertEvent(event, 'xstate.done.actor.validationCoupon');
                    const { coupon } = event.output;
                    enqueue.assign({ coupon });
                }),
                exitFlowWithError: enqueueActions(
                    ({ enqueue: _enqueue, check: _check, event: _event }) => {
                        throw new Error('Coupon not valid');
                    },
                ),
                assignFormBuilderRef: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'CONNECT_FORMBUILDER_REF');
                    enqueue.assign({ formBuilderRef: event.params });
                }),
                submitForm: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, 'SUBMIT_CLICK');
                    context.formBuilderRef?.toggleLoading(true);
                    enqueue.raise({ type: 'SUBMITTING' });
                }),
                couponRedeemedSuccessfully: enqueueActions(
                    ({ enqueue: _enqueue, event, context }) => {
                        assertEvent(event, 'xstate.done.actor.submitting');
                        context.formBuilderRef?.toggleLoading(false);
                        const schema = (context as rcFlowContextInputs).formBuilderRef?.getSchema();
                        const buttonLabel =
                            schema?.fields?.find((field) => field.id === 'waive-cooling-rights-btn')
                                ?.label || '';
                        trackCouponRedemptionSuccess(trackEvent, {
                            ctaText: t(buttonLabel)?.trim() ? t(buttonLabel) : buttonLabel,
                            coupon: context.coupon,
                            userPlan: context.userPlan,
                        });
                    },
                ),
                handleErrorWithBanner: ({ event, context }) => {
                    assertEvent(event, 'xstate.error.actor.submitting');
                    const errorMessage =
                        event.error.response?.errors?.[0]?.message ||
                        event.error.message ||
                        'oh_no_something_went_wrong';
                    context.formBuilderRef?.toggleLoading(false);
                    context.formBuilderRef?.toggleBanner({
                        message: errorMessage,
                        variant: 'critical',
                    });
                    trackCouponRedemptionFailure(trackEvent, { errorMessage });
                },
            },
            actors,
        }).createMachine({ ...(config as AnyStateConfig) });
    },

    inputActor: <
        Mapper<
            directFlowContext,
            AnyEventObject,
            Pick<directFlowContext, 'couponCode'>,
            AnyEventObject
        >
    >(({ context }) => {
        return { couponCode: context.couponCode };
    }),
};
